import { useEffect, useState } from "react";
import api, { currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Wallet, Users, TrendingUp, CheckCircle2, Clock, XCircle, ArrowUpRight, Sparkles } from "lucide-react";

const STATUS_STYLES = {
    new: "bg-blue-50 text-blue-700 border-blue-200",
    processing: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    disbursed: "bg-komma-gold/15 text-komma-navy border-komma-gold/40",
    rejected: "bg-red-50 text-red-700 border-red-200",
    closed: "bg-gray-100 text-gray-700 border-gray-200",
};

function Stat({ icon: Icon, label, value, hint, testId }) {
    return (
        <div className="komma-card p-6" data-testid={testId}>
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500">{label}</p>
                    <p className="font-serif text-3xl text-komma-navy mt-2">{value}</p>
                    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
                </div>
                <div className="h-10 w-10 rounded-md bg-komma-navy/5 text-komma-navy flex items-center justify-center">
                    <Icon size={18} />
                </div>
            </div>
        </div>
    );
}

function RoleBanner({ role, name }) {
    const map = {
        ceo: { title: "Executive Overview", tag: "CEO Console", note: "Portfolio health, pipeline value & team performance at a glance." },
        office_admin: { title: "Operations Desk", tag: "Office Admin", note: "Approvals, disbursements, and file movement across teams." },
        marketing_executive: { title: "Growth Pipeline", tag: "Marketing", note: "Leads, conversions, and campaign performance." },
        agent: { title: "Field Command", tag: "Agent", note: "Assigned files, verification queue, and today's visits." },
        telecaller: { title: "Call Queue", tag: "Telecaller", note: "Prospects to call, scheduled callbacks, and outcomes." },
        customer: { title: "My Portfolio", tag: "Customer", note: "Your loans, documents, and application status." },
    }[role] || {};
    return (
        <div className="komma-card p-8 relative overflow-hidden">
            <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-komma-gold/10" />
            <div className="absolute right-8 bottom-4 opacity-40">
                <Sparkles size={40} className="text-komma-gold" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-komma-gold">{map.tag}</p>
            <h2 className="font-serif text-3xl text-komma-navy mt-2">{map.title}</h2>
            <p className="text-sm text-gray-600 mt-2 max-w-xl">{map.note}</p>
            <p className="text-xs text-gray-500 mt-4">Signed in as <span className="font-semibold text-komma-navy">{name}</span></p>
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [loans, setLoans] = useState([]);
    const [notifs, setNotifs] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [a, l, n] = await Promise.all([
    api.get("/analytics/overview"),
    api.get("/loans"),
    api.get("/notifications"),
]);

setStats(a.data || {});

// Normalize API responses so Dashboard always receives arrays
const loanData = Array.isArray(l.data)
    ? l.data
    : Array.isArray(l.data?.loans)
        ? l.data.loans
        : Array.isArray(l.data?.data)
            ? l.data.data
            : [];

const notificationData = Array.isArray(n.data)
    ? n.data
    : Array.isArray(n.data?.notifications)
        ? n.data.notifications
        : Array.isArray(n.data?.data)
            ? n.data.data
            : [];

setLoans(loanData);
setNotifs(notificationData);
            } catch {}
        })();
    }, []);

    return (
        <div className="space-y-6" data-testid="dashboard-root">
            <RoleBanner role={user.role} name={user.name} />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={Wallet} label="Total Loans" value={stats?.total_loans ?? "—"} testId="stat-loans" />
                <Stat icon={Users} label="Customers" value={stats?.total_customers ?? "—"} testId="stat-customers" />
                <Stat icon={CheckCircle2} label="Approved" value={stats?.approved ?? "—"}
                    hint={stats ? `${stats.approved} of ${stats.total_loans}` : ""} testId="stat-approved" />
                <Stat icon={Clock} label="Pending" value={stats?.pending ?? "—"} testId="stat-pending" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="komma-card p-6 lg:col-span-2" data-testid="pipeline-card">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500">Pipeline Value</p>
                            <p className="font-serif text-4xl text-komma-navy mt-2">{currency(stats?.pipeline_value)}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500">Disbursed</p>
                            <p className="font-serif text-2xl text-komma-gold-dark mt-1">{currency(stats?.disbursed_value)}</p>
                        </div>
                    </div>
                    <div className="komma-gold-hairline my-6" />
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Recent applications</p>
                    <div className="space-y-2">
                        {loans.slice(0, 5).map((l) => (
                            <div key={l.id} className="flex items-center justify-between p-3 rounded-md bg-komma-cream/60 hover:bg-white transition-colors">
                                <div>
                                    <p className="text-sm font-semibold text-komma-navy">{l.customer_name}</p>
                                    <p className="text-xs text-gray-500 capitalize">{l.loan_type} · {l.loan_number}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-semibold text-komma-navy">{currency(l.amount)}</span>
                                    <Badge variant="outline" className={`${STATUS_STYLES[l.status] || ""} capitalize`}>{l.status}</Badge>
                                </div>
                            </div>
                        ))}
                        {loans.length === 0 && <p className="text-sm text-gray-500">No loans yet.</p>}
                    </div>
                </div>

                <div className="komma-card p-6" data-testid="notifs-card">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500">Notifications</p>
                    <p className="font-serif text-xl text-komma-navy mt-1">What's new</p>
                    <div className="komma-gold-hairline my-4" />
                    <div className="space-y-3">
                        {notifs.slice(0, 6).map((n) => (
                            <div key={n.id} className="flex gap-3 items-start">
                                <div className="h-2 w-2 mt-2 rounded-full bg-komma-gold" />
                                <div>
                                    <p className="text-sm text-komma-navy">{n.title}</p>
                                    <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-0.5">{n.role_target}</p>
                                </div>
                            </div>
                        ))}
                        {notifs.length === 0 && <p className="text-sm text-gray-500">You're all caught up.</p>}
                    </div>
                </div>
            </div>

            {(user.role === "ceo" || user.role === "office_admin") && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="komma-card p-6">
                        <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500">By Loan Type</p>
                        <div className="mt-4 space-y-3">
                            {(stats?.by_type || []).map((row) => {
                                const pct = stats.pipeline_value ? Math.round((row.amount / stats.pipeline_value) * 100) : 0;
                                return (
                                    <div key={row._id}>
                                        <div className="flex justify-between text-sm">
                                            <span className="capitalize text-komma-navy font-medium">{row._id}</span>
                                            <span className="text-gray-500">{row.count} loans · {currency(row.amount)}</span>
                                        </div>
                                        <div className="h-1.5 rounded-full bg-gray-100 mt-1.5 overflow-hidden">
                                            <div className="h-full bg-komma-gold" style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="komma-card p-6">
                        <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500">By Status</p>
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            {(stats?.by_status || []).map((s) => (
                                <div key={s._id} className="rounded-md border border-gray-200 p-3">
                                    <p className="text-xs uppercase tracking-widest text-gray-500 capitalize">{s._id}</p>
                                    <p className="font-serif text-2xl text-komma-navy mt-1">{s.count}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
